import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { uploadToPresignedUrl } from '@/lib/upload/uploadToPresignedUrl.ts';
import { resolveContentType } from '@/lib/upload/resolveContentType.ts';
import rawBankTransactionsApi from '@/features/dashboard/rawBankTransactions/api/rawBankTransactionsApi.ts';
import { BankTransactionImportResultDto } from '@/features/dashboard/rawBankTransactions/types/BankTransactionImportResultDto.ts';
import { Bank } from '@/features/dashboard/rawBankTransactions/types/Bank.ts';
import { invalidateFinanceRelatedQueries } from '@/utils/invalidateFinanceQueries.ts';

interface ImportBankTransactionsVars {
  file: File;
  bank: Bank;
}

export default function useImportBankTransactions() {
  const queryClient = useQueryClient();

  return useMutation<
    BankTransactionImportResultDto,
    Error,
    ImportBankTransactionsVars
  >({
    mutationFn: async ({ file, bank }) => {
      const contentType = resolveContentType(file);

      const initResponse = await rawBankTransactionsApi.initUpload({
        fileName: file.name,
        contentType,
        fileSize: file.size,
        bank,
      });

      await uploadToPresignedUrl(initResponse.uploadUrl, file, contentType);

      return rawBankTransactionsApi.processUpload(initResponse.fileId);
    },
    onSuccess: (data) => {
      invalidateFinanceRelatedQueries(queryClient);
      toast.success(`Zaimportowano ${data.importedCount} transakcji`, {
        description:
          data.duplicateCount > 0
            ? `Pominięto ${data.duplicateCount} duplikatów`
            : undefined,
      });
    },
    onError: (error: Error) => {
      toast.error('Błąd importu', {
        description:
          error.message || 'Wystąpił błąd podczas importowania pliku.',
      });
    },
  });
}
